import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Print,
  Send,
  Inventory as InventoryIcon,
  Person as PersonIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import ViewInvoiceModal from '../Modals/ViewInvoiceModal';
import { generateInvoiceNumber, calculateTaxes, convertAmountToWords } from '../../utils/invoiceUtils';
import { profileAPI, authFetch } from '../../utils/api';
import { inventoryAPI } from '../../utils/inventoryApi';
import { inventoryManager } from '../../utils/inventoryManager';
import ProductSelector from './ProductSelector';
import CustomerSelector from './CustomerSelector';
import { extractGSTFromInclusivePrice } from '../../utils/gstCalculation';
import { INDIAN_STATES, getStateFromGSTIN, isInterstateTransaction } from '../../utils/indianStates';

// Common units for inventory items
const COMMON_UNITS = [
  'PCS', 'NOS', 'KG', 'GMS', 'LTR', 'ML', 'MTR', 'CM', 'FT', 'INCH',
  'BOX', 'CARTON', 'PACKET', 'BUNDLE', 'SET', 'PAIR', 'DOZEN',
  'HOUR', 'DAY', 'MONTH', 'YEAR', 'SQM', 'CUM', 'TON', 'QUINTAL'
];

const DEFAULT_INVOICE_TERMS = "1. Goods once sold will not be taken back.\n2. Interest @ 18% p.a. will be charged if payment is delayed beyond 30 days.\n3. Subject to local jurisdiction.\n4. E.& O.E.";
const DEFAULT_QUOTATION_TERMS = "1. This quotation is valid for 30 days from the date of issue.\n2. Prices are subject to change without prior notice.\n3. Goods once sold will not be taken back or exchanged.\n4. Subject to local jurisdiction.";

const CreateInvoiceForm = ({ onSubmit, initialData, invoiceId }) => {
  const navigate = useNavigate();
  // Dynamic company details state
  const [companyDetails, setCompanyDetails] = useState({
    name: '',
    logo: '',
    address: '',
    mobile: '',
    gstin: '',
    email: '',
    state: '',
  });

  // Dynamic bank details state
  const [bankDetails, setBankDetails] = useState({
    name: '',
    accountNo: '',
    ifscCode: '',
    branch: '',
    holderName: '',
  });

  // Customer details state
  const [billTo, setBillTo] = useState({
    name: '',
    address: '',
    state: '',
    mobile: '',
    gstin: '',
    customer_id: '',
  });

  // Ship to details state
  const [shipTo, setShipTo] = useState({
    name: '',
    address: '',
    mobile: '',
  });

  // Transport details state
  const [transportDetails, setTransportDetails] = useState({
    vehicleNumber: '',
    vehicleType: '',
    driverName: '',
    driverContact: '',
    ewayBillNumber: '',
  });

  // Invoice items state
  const [items, setItems] = useState([{
    name: '',
    description: '',
    quantity: 0,
    unit: 'PCS',
    rate: 0,
    tax: 0,
    discount: 0,
    amount: 0,
    hsn: '',
    inventoryId: null, // Track inventory item ID
    currentStock: 0, // Track current stock level
    stockStatus: 'active' // Track stock status
  }]);

  // Additional invoice state
  const [discount, setDiscount] = useState(0);
  const [termsAndConditions, setTermsAndConditions] = useState('');
  const [originalFor, setOriginalFor] = useState('');
  const [documentType, setDocumentType] = useState('invoice');
  const [theme, setTheme] = useState('classic');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [signature, setSignature] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [showCustomerSelector, setShowCustomerSelector] = useState(false);
  const [stockValidationErrors, setStockValidationErrors] = useState([]);
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [previewInvoiceData, setPreviewInvoiceData] = useState(null);

  // Canvas ref for signature
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef({ x: 0, y: 0 });
  const [showSignatureImage, setShowSignatureImage] = useState(true);

  // Function to validate stock availability
  const validateStockAvailability = async (itemsToValidate) => {
    const inventoryItems = itemsToValidate.filter(item => item.inventoryId);

    if (inventoryItems.length === 0) return { isValid: true, errors: [] };

    try {
      const stockCheck = await inventoryAPI.checkStockAvailability(
        inventoryItems.map(item => ({
          id: item.inventoryId,
          quantity: item.quantity
        })),
        invoiceId // Pass invoiceId to check available stock correctly
      );

      if (stockCheck.success === false) {
        return {
          isValid: false,
          errors: stockCheck.errors || ['Insufficient stock for some items']
        };
      }

      return { isValid: true, errors: [] };
    } catch (error) {
      console.error('Stock validation error:', error);
      // If API fails, allow proceeding but show warning
      return {
        isValid: true,
        errors: ['Could not verify stock availability. Please check inventory levels manually.']
      };
    }
  };

  // Function to deduct inventory quantities (only when invoice is finalized)
  const deductInventoryStock = async (itemsToDeduct, invoiceId, invoiceStatus = 'finalized') => {
    const inventoryItems = itemsToDeduct.filter(item => item.inventoryId);

    if (inventoryItems.length === 0) return;

    try {
      // Check if inventory has already been processed for this invoice
      const isProcessed = await inventoryAPI.checkInventoryProcessed(invoiceId);
      if (isProcessed && isProcessed.processed) {
        console.log('Inventory already processed for this invoice, skipping deduction');
        return;
      }

      // Only deduct if invoice is being finalized (not draft)
      if (invoiceStatus === 'finalized' || invoiceStatus === 'paid') {
        await inventoryAPI.bulkUpdateQuantities(
          inventoryItems.map(item => ({
            id: item.inventoryId,
            quantity: item.quantity
          })),
          invoiceId,
          'deduct'
        );

        // Mark invoice as processed to prevent duplicate deductions
        await inventoryAPI.markInventoryProcessed(invoiceId);

        console.log('Inventory stock deducted successfully for invoice:', invoiceId);
      }
    } catch (error) {
      console.error('Failed to deduct inventory stock:', error);
      // Don't throw error, just log it - invoice creation should continue
    }
  };

  // Function to restore inventory when invoice is cancelled
  const restoreInventoryStock = async (itemsToRestore, invoiceId, reason = 'Invoice cancelled') => {
    const inventoryItems = itemsToRestore.filter(item => item.inventoryId);

    if (inventoryItems.length === 0) return;

    try {
      await inventoryAPI.restoreStockForInvoice(inventoryItems, invoiceId, reason);
      console.log('Inventory stock restored successfully for invoice:', invoiceId);
    } catch (error) {
      console.error('Failed to restore inventory stock:', error);
      // Don't throw error, just log it
    }
  };

  // Normalize unit helper to keep UI values consistent with dropdown options
  const normalizeUnit = (unit) => {
    if (!unit) return 'PCS';
    const key = String(unit).trim().toLowerCase();
    const map = {
      pcs: 'PCS', nos: 'PCS', piece: 'PCS', pieces: 'PCS',
      cm: 'CM',
      mtr: 'MTR', meter: 'MTR', metre: 'MTR',
      inch: 'INCH', in: 'INCH',
      ft: 'FT', foot: 'FT', feet: 'FT',
      kg: 'KG', kilogram: 'KG', kilograms: 'KG',
      g: 'GMS', gram: 'GMS', grams: 'GMS', gms: 'GMS',
      ltr: 'LTR', liter: 'LTR', litre: 'LTR',
      ml: 'ML',
      box: 'BOX', carton: 'CARTON', packet: 'PACKET', bundle: 'BUNDLE',
      set: 'SET', pair: 'PAIR', dozen: 'DOZEN',
      hour: 'HOUR', day: 'DAY', month: 'MONTH', year: 'YEAR',
      sqm: 'SQM', sq_m: 'SQM', cum: 'CUM', ton: 'TON', quintal: 'QUINTAL',
    };
    return map[key] || String(unit).trim().toUpperCase();
  };

  const [productSelectorOpen, setProductSelectorOpen] = useState(false);
  const [customerSelectorOpen, setCustomerSelectorOpen] = useState(false);

  // Recalculate initial item amount when component mounts
  useEffect(() => {
    if (items.length > 0 && items[0].quantity && items[0].rate) {
      const firstItem = items[0];
      const baseAmount = firstItem.quantity * firstItem.rate;
      const discountAmount = (baseAmount * (firstItem.discount || 0)) / 100;
      const discountedAmount = baseAmount - discountAmount;
      const taxRate = firstItem.tax || 0;
      const taxAmount = discountedAmount * (taxRate / 100);
      const totalWithTax = discountedAmount + taxAmount;

      setItems(prev => [{
        ...prev[0],
        amount: isNaN(totalWithTax) ? 0 : totalWithTax
      }, ...prev.slice(1)]);
    }
  }, []);

  // Function to fetch dynamic company details
  const fetchCompanyDetails = React.useCallback(async () => {
    try {
      const accountId = localStorage.getItem('tenantId');
      let businessInfo = null;

      if (accountId) {
        try {
          businessInfo = await profileAPI.getBusinessInfo(accountId);
        } catch (e) {
          console.warn('Could not fetch business info by accountId, trying fallback:', e);
        }
      }

      if (!businessInfo || (!businessInfo.business_name && !businessInfo.business_state)) {
        try {
          businessInfo = await profileAPI.getBusinessInfo();
        } catch (e) {
          console.warn('Could not fetch direct business info:', e);
        }
      }

      if (businessInfo) {
        let detectedCompState = businessInfo.business_state || '';
        if (!detectedCompState && businessInfo.business_gstin) {
          const match = getStateFromGSTIN(businessInfo.business_gstin);
          if (match) detectedCompState = match.name;
        }

        setCompanyDetails(prev => ({
          ...prev, // Keep existing state
          name: businessInfo.business_name || prev.name,
          logo: businessInfo.business_logo || prev.logo,
          address: businessInfo.business_address || prev.address,
          gstin: businessInfo.business_gstin || prev.gstin,
          email: businessInfo.business_email || prev.email,
          mobile: businessInfo.business_mobile || prev.mobile,
          state: detectedCompState || prev.state,
        }));
      }
    } catch (error) {
      console.error('Error fetching company details:', error);
    }
  }, []);

  // Function to fetch dynamic bank details
  const fetchBankDetails = React.useCallback(async () => {
    try {
      const accountId = localStorage.getItem('tenantId');
      let businessInfo = null;

      if (accountId) {
        try {
          businessInfo = await profileAPI.getBusinessInfo(accountId);
        } catch (e) {}
      }

      if (!businessInfo || !businessInfo.bank_account_number) {
        try {
          businessInfo = await profileAPI.getBusinessInfo();
        } catch (e) {}
      }

      if (businessInfo) {
        setBankDetails(prev => ({
          ...prev,
          holderName: prev.holderName || businessInfo.bank_holder_name || '',
          name: prev.name || businessInfo.bank_name || '',
          accountNo: prev.accountNo || businessInfo.bank_account_number || '',
          ifscCode: prev.ifscCode || businessInfo.bank_ifsc_code || '',
          branch: prev.branch || businessInfo.bank_branch || '',
        }));
      }
    } catch (error) {
      console.error('Error fetching bank details:', error);
    }
  }, []);

  // Load dynamic data on component mount
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchCompanyDetails(), fetchBankDetails()]);
    };
    loadData();
  }, [fetchCompanyDetails, fetchBankDetails]);

  // Prefill when editing
  useEffect(() => {
    if (!initialData) {
      console.log('CreateInvoiceForm: No initialData provided');
      return;
    }

    console.log('CreateInvoiceForm: Applying initialData:', initialData);

    if (initialData.companyDetails) {
      console.log('Setting company details from initialData');
      setCompanyDetails(prev => ({ ...prev, ...initialData.companyDetails }));
    }

    if (initialData.bankDetails) {
      console.log('Setting bank details from initialData');
      setBankDetails(prev => ({ ...prev, ...initialData.bankDetails }));
    }

    if (initialData.billTo) {
      console.log('Setting billTo from initialData');
      setBillTo({
        ...initialData.billTo,
        customer_id: initialData.billTo.customer_id && !isNaN(initialData.billTo.customer_id) ? initialData.billTo.customer_id : null,
      });
    }

    if (initialData.shipTo) {
      console.log('Setting shipTo from initialData');
      setShipTo(initialData.shipTo);
    }

    if (initialData.transportDetails) {
      console.log('Setting transportDetails from initialData');
      setTransportDetails(initialData.transportDetails);
    }

    if (Array.isArray(initialData.items) && initialData.items.length > 0) {
      console.log('Setting items from initialData:', initialData.items.length);
      // Ensure all items have proper default values for all properties
      const normalizedItems = initialData.items.map(item => ({
        name: item.name || '',
        description: item.description || '',
        quantity: item.quantity || 0,
        rate: item.rate || 0,
        tax: item.tax || 0,
        discount: item.discount || 0,
        amount: item.amount || 0,
        hsn: item.hsn || '',
        unit: normalizeUnit(item.unit || 'PCS'),
        inventoryId: item.inventoryId || null,
        ...item // Spread to preserve any additional properties
      }));
      setItems(normalizedItems);
    }

    if (typeof initialData.originalFor !== 'undefined') setOriginalFor(initialData.originalFor || '');
    if (typeof initialData.documentType !== 'undefined') setDocumentType(initialData.documentType || 'invoice');
    if (typeof initialData.theme !== 'undefined') setTheme(initialData.theme || 'classic');
    if (typeof initialData.termsAndConditions !== 'undefined') setTermsAndConditions(initialData.termsAndConditions || '');
    if (initialData.signatureUrl) {
      try {
        setSignature(initialData.signatureUrl);
      } catch { }
    }

    // Normalize date to yyyy-MM-dd for the date input
    const rawDate = initialData.invoiceDate || initialData.invoice_date || initialData.date;
    if (rawDate) {
      try {
        // If already yyyy-MM-dd keep as is; else parse to ISO local date
        const normalized = /\d{4}-\d{2}-\d{2}/.test(rawDate)
          ? rawDate
          : new Date(rawDate).toISOString().split('T')[0];
        setInvoiceDate(normalized);
      } catch {
        setInvoiceDate(new Date().toISOString().split('T')[0]);
      }
    }
  }, [initialData]);

  // Initialize signature canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const setCanvasSize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const ratio = window.devicePixelRatio || 1;
      canvas.width = parent.clientWidth * ratio;
      canvas.height = 180 * ratio;
      canvas.style.width = `${parent.clientWidth}px`;
      canvas.style.height = '180px';
      ctx.scale(ratio, ratio);
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#000';
    };
    setCanvasSize();
    window.addEventListener('resize', setCanvasSize);

    const getPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      if (e.touches && e.touches[0]) {
        return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
      }
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const start = (e) => {
      isDrawingRef.current = true;
      setShowSignatureImage(false);
      lastPointRef.current = getPos(e);
      e.preventDefault();
    };
    const move = (e) => {
      if (!isDrawingRef.current) return;
      const pt = getPos(e);
      ctx.beginPath();
      ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
      ctx.lineTo(pt.x, pt.y);
      ctx.stroke();
      lastPointRef.current = pt;
      e.preventDefault();
    };
    const end = () => {
      if (!isDrawingRef.current) return;
      isDrawingRef.current = false;
      try {
        // Export with a white background to avoid black boxes in PDFs
        const exportCanvas = document.createElement('canvas');
        exportCanvas.width = canvas.width;
        exportCanvas.height = canvas.height;
        const exportCtx = exportCanvas.getContext('2d');
        exportCtx.fillStyle = '#FFFFFF';
        exportCtx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
        exportCtx.drawImage(canvas, 0, 0);
        // Use PNG to preserve crisp strokes and avoid JPEG transparency issues
        const dataUrl = exportCanvas.toDataURL('image/png');
        setSignature(dataUrl);
      } catch { }
    };

    // Mouse
    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    canvas.addEventListener('mouseup', end);
    canvas.addEventListener('mouseleave', end);
    // Touch
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', end);

    return () => {
      window.removeEventListener('resize', setCanvasSize);
      canvas.removeEventListener('mousedown', start);
      canvas.removeEventListener('mousemove', move);
      canvas.removeEventListener('mouseup', end);
      canvas.removeEventListener('mouseleave', end);
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove', move);
      canvas.removeEventListener('touchend', end);
    };
  }, []);

  const handleDocumentTypeChange = (e) => {
    setDocumentType(e.target.value);
  };

  const handleThemeChange = (e) => {
    setTheme(e.target.value);
  };

  const handleSaveDefaultTerms = (type) => {
    if (type === 'invoice') {
      localStorage.setItem('userDefaultInvoiceTerms', termsAndConditions);
      alert('Current text saved as your default Invoice terms!');
    } else {
      localStorage.setItem('userDefaultQuotationTerms', termsAndConditions);
      alert('Current text saved as your default Quotation terms!');
    }
  };

  const loadDefaultTerms = (type) => {
    if (type === 'invoice') {
      const saved = localStorage.getItem('userDefaultInvoiceTerms');
      setTermsAndConditions(saved || DEFAULT_INVOICE_TERMS);
    } else {
      const saved = localStorage.getItem('userDefaultQuotationTerms');
      setTermsAndConditions(saved || DEFAULT_QUOTATION_TERMS);
    }
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('Signature image must be less than 2MB.');
        e.target.value = '';
        return;
      }
      if (!['image/jpeg', 'image/jpg'].includes(file.type)) {
        alert('Only JPG/JPEG formats are allowed for signatures.');
        e.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        setSignature(event.target.result);
        setShowSignatureImage(false);
        
        // Also draw the uploaded image to the canvas so it's visible
        const canvas = canvasRef.current;
        if (canvas) {
          const ctx = canvas.getContext('2d');
          const img = new Image();
          img.onload = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            // Calculate aspect ratio to fit image nicely
            const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
            const x = (canvas.width / 2) - (img.width / 2) * scale;
            const y = (canvas.height / 2) - (img.height / 2) * scale;
            ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
          };
          img.src = event.target.result;
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignature('');
    setShowSignatureImage(false);
  };

  const handleCompanyDetailsChange = (field) => (event) => {
    const val = event.target.value;
    setCompanyDetails(prev => {
      const updated = { ...prev, [field]: val };
      if (field === 'gstin') {
        const clean = val.trim().toUpperCase();
        if (clean.length >= 2) {
          const match = getStateFromGSTIN(clean);
          if (match && (!prev.state || prev.state === '')) {
            updated.state = match.name;
          }
        }
      }
      return updated;
    });
  };

  const handleBillToChange = (field) => (event) => {
    const val = event.target.value;
    setBillTo(prev => {
      const updated = { ...prev, [field]: val };
      if (field === 'gstin') {
        const clean = val.trim().toUpperCase();
        if (clean.length >= 2) {
          const match = getStateFromGSTIN(clean);
          if (match && (!prev.state || prev.state === '')) {
            updated.state = match.name;
          }
        }
      }
      return updated;
    });
  };

  const handleShipToChange = (field) => (event) => {
    const val = event.target.value;
    setShipTo(prev => {
      const updated = { ...prev, [field]: val };
      if (field === 'gstin') {
        const clean = val.trim().toUpperCase();
        if (clean.length >= 2) {
          const match = getStateFromGSTIN(clean);
          if (match && (!prev.state || prev.state === '')) {
            updated.state = match.name;
          }
        }
      }
      return updated;
    });
  };

  const handleTransportDetailsChange = (field) => (event) => {
    setTransportDetails({ ...transportDetails, [field]: event.target.value });
  };

  const handleSanitizedNumberChange = (index, field, value) => {
    let sanitizedValue = value;

    // Allow valid decimals like "0.2" but not "012" or "034"
    if (/^0\d+/.test(sanitizedValue)) {
      sanitizedValue = sanitizedValue.replace(/^0+/, '');
    }

    // If empty, reset to "0"
    if (sanitizedValue === '') {
      sanitizedValue = '0';
    }

    // Cap discount and GST at 100%
    if (field === 'discount' || field === 'tax') {
      const numValue = parseFloat(sanitizedValue) || 0;
      sanitizedValue = Math.min(100, Math.max(0, numValue)).toString();
    }

    // Ensure quantity and rate are positive
    // Ensure quantity and rate are positive integers
    if (field === 'quantity') {
      const numValue = parseFloat(sanitizedValue) || 0;
      // Quantity must be integer
      sanitizedValue = Math.max(1, Math.round(numValue)).toString();
    } else if (field === 'rate') {
      const numValue = parseFloat(sanitizedValue) || 0;
      // Rate must be integer
      sanitizedValue = Math.max(0, Math.round(numValue)).toString();
    }

    handleItemChange(index, field)(sanitizedValue);
  };

  const handleItemAmountChange = (index, totalAmount) => {
    const newItems = [...items];
    const numTotalAmount = parseFloat(totalAmount) || 0;

    newItems[index] = {
      ...newItems[index],
      amount: numTotalAmount,
    };

    setItems(newItems);
  };

  const handleItemChange = (index, field) => (value) => {
    const newItems = [...items];
    const inputValue = typeof value === 'string' ? value : value.target.value;
    let numValue;

    if (field === 'tax' || field === 'discount') {
      // Tax, discount can be decimal
      numValue = parseFloat(inputValue) || 0;
    } else if (field === 'quantity' || field === 'rate') {
      // Quantity and Rate must be integer
      numValue = Math.round(parseFloat(inputValue)) || 0;
    } else {
      numValue = inputValue;
    }

    // Store previous inventory ID for inventory tracking
    const previousInventoryId = newItems[index].inventoryId;

    if (field === 'unit') {
      newItems[index] = {
        ...newItems[index],
        unit: normalizeUnit(inputValue),
      };
    } else {
      newItems[index] = {
        ...newItems[index],
        [field]: numValue,
      };
    }

    // Calculate amount including GST whenever quantity, rate, tax, or discount changes
    if (field === 'quantity' || field === 'rate' || field === 'tax' || field === 'discount') {
      const quantity = newItems[index].quantity || 0;
      const rate = newItems[index].rate || 0;
      const discount = Math.min(100, newItems[index].discount || 0); // Cap discount at 100%
      const tax = newItems[index].tax || 0;

      // Update the discount field to reflect the capped value
      newItems[index].discount = discount;

      // Validate inputs to prevent invalid calculations
      if (quantity <= 0 || rate <= 0) {
        newItems[index].amount = 0;
        console.log(`Item ${index}: Invalid quantity (${quantity}) or rate (${rate}), setting amount to 0`);
        setItems(newItems);
        return;
      }

      const baseAmount = quantity * rate;
      const discountAmount = (baseAmount * discount) / 100;
      const discountedAmount = Math.max(0, baseAmount - discountAmount); // Ensure amount doesn't go negative
      const taxAmount = discountedAmount * (tax / 100);
      const totalWithTax = discountedAmount + taxAmount;

      // Store the amount INCLUDING tax for UI display - ROUNDED TO INTEGER
      newItems[index].amount = isNaN(totalWithTax) ? 0 : Math.round(totalWithTax);

      // Debug logging
      console.log(`Item ${index} calculation:`, {
        quantity,
        rate,
        discount,
        tax,
        baseAmount,
        discountAmount,
        discountedAmount,
        taxAmount,
        totalWithTax,
        finalAmount: newItems[index].amount
      });
    }

    // Real-time inventory management when quantity changes
    if (field === 'quantity' && previousInventoryId) {
      const newQuantity = newItems[index].quantity || 0;

      // Get the original stock from inventory (not the current displayed stock)
      const originalStock = newItems[index].originalStock || newItems[index].currentStock || 0;

      // Calculate remaining stock correctly
      const remainingStock = Math.max(0, originalStock - newQuantity);

      // Update current stock display
      newItems[index].currentStock = remainingStock;
      newItems[index].originalStock = originalStock; // Store original stock

      // Update stock status based on remaining stock
      if (remainingStock === 0) {
        newItems[index].stockStatus = 'out_of_stock';
      } else if (remainingStock <= 5) {
        newItems[index].stockStatus = 'low_stock';
      } else {
        newItems[index].stockStatus = 'active';
      }

      // NOTE: Removed real-time API call to avoid stock validation errors during input
      // Stock validation should only happen on invoice finalization, not during input

      console.log(`Real-time inventory display update for item ${index}:`, {
        inventoryId: previousInventoryId,
        originalStock,
        newQuantity,
        remainingStock,
        stockStatus: newItems[index].stockStatus
      });
    }

    setItems(newItems);
  };

  // Reverse GST Calculator - Calculate base price from GST inclusive price
  const handleReverseCalc = (index, inclusivePrice) => {
    // ... (rest of the code remains the same)
    const newItems = [...items];
    const tax = newItems[index].tax || 0;

    console.log(`Reverse calc input - Item ${index}:`, {
      inclusivePrice,
      tax,
      currentRate: newItems[index].rate
    });

    if (tax > 0 && inclusivePrice > 0) {
      // Calculate base price: Base = Inclusive / (1 + Tax/100)
      const basePrice = inclusivePrice / (1 + tax / 100);
      const gstAmount = inclusivePrice - basePrice;

      newItems[index].rate = parseFloat(basePrice.toFixed(2));

      // Recalculate amount with the new rate
      const quantity = newItems[index].quantity || 1; // Default to 1 if not set
      const discount = newItems[index].discount || 0;

      const baseAmount = quantity * basePrice;
      const discountAmount = (baseAmount * discount) / 100;
      const discountedAmount = Math.max(0, baseAmount - discountAmount);
      const taxAmount = discountedAmount * (tax / 100);
      const totalWithTax = discountedAmount + taxAmount;

      newItems[index].amount = isNaN(totalWithTax) ? 0 : parseFloat(totalWithTax.toFixed(2));

      console.log(`Reverse calc result for item ${index}:`, {
        inclusivePrice,
        tax,
        basePrice: basePrice.toFixed(2),
        gstAmount: gstAmount.toFixed(2),
        quantity,
        baseAmount: baseAmount.toFixed(2),
        discountAmount: discountAmount.toFixed(2),
        discountedAmount: discountedAmount.toFixed(2),
        taxAmount: taxAmount.toFixed(2),
        totalWithTax: totalWithTax.toFixed(2),
        newRate: basePrice.toFixed(2)
      });
    } else {
      console.log(`Reverse calc skipped - Invalid inputs:`, {
        tax,
        inclusivePrice
      });
    }

    setItems(newItems);
  };

  // Toggle reverse calculator mode for an item
  const toggleReverseCalc = (index) => {
    const newItems = [...items];
    const wasReverseMode = newItems[index].reverseCalc;
    newItems[index].reverseCalc = !newItems[index].reverseCalc;

    if (!wasReverseMode) {
      // Switching TO reverse mode - calculate and show GST-inclusive price
      const baseRate = newItems[index].rate || 0;
      const taxRate = newItems[index].tax || 0;
      const inclusivePrice = baseRate + (baseRate * taxRate / 100);
      newItems[index].reverseCalcValue = inclusivePrice.toFixed(2);
      console.log(`Switched to reverse mode - showing GST inclusive price:`, {
        baseRate,
        taxRate,
        inclusivePrice: inclusivePrice.toFixed(2)
      });
    } else {
      // Switching FROM reverse mode - clear the reverse calc value
      delete newItems[index].reverseCalcValue;
      console.log(`Switched to normal mode - cleared reverse calc value`);
    }

    console.log(`Toggled reverse calc for item ${index}:`, newItems[index].reverseCalc);
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, {
      name: '',
      description: '',
      quantity: 0,
      unit: 'PCS',
      rate: 0,
      tax: 0,
      discount: 0,
      amount: 0,
      hsn: '',
      inventoryId: null, // Track inventory item ID
      currentStock: 0, // Track current stock level
      originalStock: 0, // Track original stock level
      stockStatus: 'active' // Track stock status
    }]);
  };

  const handleProductSelect = (product) => {
    const normalized = { ...product, unit: normalizeUnit(product.unit) };

    // Real-time inventory tracking when product is added
    if (product.inventoryId && product.quantity > 0) {
      console.log(`Real-time inventory display update for added product:`, {
        inventoryId: product.inventoryId,
        productName: product.name,
        quantityAdded: product.quantity,
        currentStock: product.currentStock,
        remainingStock: Math.max(0, product.currentStock - product.quantity)
      });

      // Store original stock and calculate remaining stock correctly
      const originalStock = product.currentStock || 0;
      const remainingStock = Math.max(0, originalStock - product.quantity);

      normalized.originalStock = originalStock; // Store original stock
      normalized.currentStock = remainingStock; // Show remaining stock

      // Update stock status based on remaining stock
      if (remainingStock === 0) {
        normalized.stockStatus = 'out_of_stock';
      } else if (remainingStock <= 5) {
        normalized.stockStatus = 'low_stock';
      } else {
        normalized.stockStatus = 'active';
      }

      // NOTE: Removed real-time API call to avoid stock validation errors during product selection
      // Stock validation should only happen on invoice finalization
    }

    setItems(prev => {
      // If there is only one item and it's completely empty, replace it
      if (prev.length === 1 && !prev[0].name && !prev[0].quantity && !prev[0].rate) {
        return [normalized];
      }
      return [...prev, normalized];
    });
  };

  const handleCustomerSelect = (customer) => {
    let customerState = customer.state || '';
    if (!customerState && customer.gstin) {
      const match = getStateFromGSTIN(customer.gstin);
      if (match) customerState = match.name;
    }
    setBillTo({
      name: customer.name || '',
      address: customer.address || '',
      state: customerState,
      mobile: customer.mobile || '',
      gstin: customer.gstin || '',
      customer_id: customer.customer_id && !isNaN(customer.customer_id) ? customer.customer_id : null,
    });
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      const itemToRemove = items[index];

      // Real-time inventory restoration when item is removed
      if (itemToRemove.inventoryId && itemToRemove.quantity > 0) {
        console.log(`Real-time inventory display restoration for removed item:`, {
          inventoryId: itemToRemove.inventoryId,
          quantityBeingRestored: itemToRemove.quantity,
          itemName: itemToRemove.name,
          originalStock: itemToRemove.originalStock,
          currentDisplayStock: itemToRemove.currentStock
        });

        // NOTE: Removed real-time API call to avoid stock validation errors during item removal
        // Stock validation should only happen on invoice finalization
      }

      setItems(items.filter((_, i) => i !== index));
    }
  };

  const handleBankDetailsChange = (field) => (event) => {
    setBankDetails({ ...bankDetails, [field]: event.target.value });
  };

  const handleSubmitInvoice = async (invoice) => {
    if (isLoading) return; // Prevent duplicate submissions

    try {
      setIsLoading(true);
      console.log('handleSubmitInvoice called with invoice:', invoice);
      // Delegate creation to parent; do not call API or navigate here
      if (onSubmit) {
        console.log('Calling onSubmit with invoice:', invoice);
        onSubmit(invoice);
      } else {
        console.log('No onSubmit function provided');
      }
    } catch (error) {
      console.error('Error preparing invoice:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Live calculations for Invoice Summary, GST Breakdown and Document Preview
  const calculatedTotals = React.useMemo(() => {
    let totalQty = 0;
    let grossAmount = 0;
    let totalDiscount = 0;
    let taxableAmount = 0;
    let totalTax = 0;
    const taxBreakdown = {};

    (items || []).forEach(item => {
      const qty = Number(item.quantity) || 0;
      const rate = Number(item.rate) || 0;
      const disc = Math.min(100, Math.max(0, Number(item.discount) || 0));
      const taxRate = Math.max(0, Number(item.tax) || 0);

      const base = qty * rate;
      const itemDiscAmt = (base * disc) / 100;
      const discounted = Math.max(0, base - itemDiscAmt);
      const itemTaxAmt = (discounted * taxRate) / 100;

      totalQty += qty;
      grossAmount += base;
      totalDiscount += itemDiscAmt;
      taxableAmount += discounted;
      totalTax += itemTaxAmt;

      if (taxRate > 0) {
        if (!taxBreakdown[taxRate]) {
          taxBreakdown[taxRate] = { taxable: 0, tax: 0 };
        }
        taxBreakdown[taxRate].taxable += discounted;
        taxBreakdown[taxRate].tax += itemTaxAmt;
      }
    });

    const originState = (companyDetails.state || getStateFromGSTIN(companyDetails.gstin)?.name || '').trim();
    // Bill To is primary place of supply under GST Sec 10(1)(b); fallback to Ship To if billTo state/gstin is unspecified
    const destState = (
      billTo.state ||
      getStateFromGSTIN(billTo.gstin)?.name ||
      shipTo.state ||
      getStateFromGSTIN(shipTo.gstin)?.name ||
      ''
    ).trim();

    const isInter = isInterstateTransaction(
      companyDetails.state || companyDetails.gstin,
      billTo.state || billTo.gstin || shipTo.state || shipTo.gstin
    );

    let cgst = 0;
    let sgst = 0;
    let igst = 0;

    if (isInter) {
      igst = isNaN(totalTax) ? 0 : totalTax;
      cgst = 0;
      sgst = 0;
    } else {
      cgst = isNaN(totalTax / 2) ? 0 : totalTax / 2;
      sgst = isNaN(totalTax / 2) ? 0 : totalTax / 2;
      igst = 0;
    }

    const rawTotal = taxableAmount + totalTax;
    const roundedTotal = isNaN(rawTotal) ? 0 : Math.round(rawTotal);
    const roundOff = Number((roundedTotal - rawTotal).toFixed(2));
    const words = convertAmountToWords(roundedTotal || 0);

    return {
      totalQty,
      itemCount: (items || []).filter(i => (i.name || i.description || '').trim()).length,
      grossAmount,
      totalDiscount,
      taxableAmount,
      totalTax,
      originState,
      destState,
      isInter,
      cgst,
      sgst,
      igst,
      rawTotal,
      roundedTotal,
      roundOff,
      taxBreakdown,
      words
    };
  }, [items, companyDetails.state, companyDetails.gstin, billTo.state, billTo.gstin, shipTo.state, shipTo.gstin]);

  const handleOpenPreview = () => {
    const previewData = {
      company_name: companyDetails.name,
      company_address: companyDetails.address,
      company_state: companyDetails.state,
      company_gstin: companyDetails.gstin,
      company_email: companyDetails.email,
      company_mobile: companyDetails.mobile,
      company_logo: companyDetails.logo || null,
      bill_to_name: billTo.name,
      bill_to_address: billTo.address,
      bill_to_state: billTo.state,
      bill_to_mobile: billTo.mobile,
      bill_to_gstin: billTo.gstin,
      ship_to_name: shipTo.name || billTo.name,
      ship_to_address: shipTo.address || billTo.address,
      ship_to_state: shipTo.state || billTo.state,
      ship_to_mobile: shipTo.mobile || billTo.mobile,
      ship_to_gstin: shipTo.gstin || billTo.gstin,
      items: (items || []).map((item, idx) => ({
        name: item.name || item.description || `Item ${idx + 1}`,
        description: item.description || item.name || `Item ${idx + 1}`,
        quantity: Number(item.quantity || 0),
        rate: Number(item.rate || 0),
        tax: Number(item.tax || 0),
        discount: Number(item.discount || 0),
        amount: Number(item.amount || 0),
        hsn: item.hsn || '',
        unit: item.unit || 'PCS'
      })),
      subtotal: calculatedTotals.taxableAmount,
      total: calculatedTotals.roundedTotal,
      total_discount: calculatedTotals.totalDiscount,
      gst: calculatedTotals.totalTax,
      cgst: calculatedTotals.cgst,
      sgst: calculatedTotals.sgst,
      igst: calculatedTotals.igst,
      invoice_number: generateInvoiceNumber(),
      invoice_date: invoiceDate,
      document_type: documentType || 'invoice',
      original_for: originalFor || '',
      theme: theme || 'classic',
      watermark: previewInvoiceData?.watermark || 'none',
      terms: termsAndConditions,
      amount_in_words: calculatedTotals.words,
      bank_name: bankDetails.name,
      bank_account_number: bankDetails.accountNo,
      bank_ifsc_code: bankDetails.ifscCode,
      bank_branch: bankDetails.branch,
      bank_holder_name: bankDetails.holderName,
      vehicle_number: transportDetails.vehicleNumber,
      vehicle_type: transportDetails.vehicleType,
      driver_name: transportDetails.driverName,
      driver_contact: transportDetails.driverContact,
      signature: signature || '',
      signatureUrl: signature || '',
      company: {
        name: companyDetails.name,
        address: companyDetails.address,
        state: companyDetails.state,
        mobile: companyDetails.mobile,
        gstin: companyDetails.gstin,
        email: companyDetails.email,
        logo: companyDetails.logo || null
      },
      billTo: {
        name: billTo.name,
        address: billTo.address,
        state: billTo.state,
        mobile: billTo.mobile,
        gstin: billTo.gstin
      },
      shipTo: {
        name: shipTo.name || billTo.name,
        address: shipTo.address || billTo.address,
        state: shipTo.state || billTo.state,
        mobile: shipTo.mobile || billTo.mobile,
        gstin: shipTo.gstin || billTo.gstin
      },
      taxDetails: {
        cgst: calculatedTotals.cgst,
        sgst: calculatedTotals.sgst,
        igst: calculatedTotals.igst,
        isInterstate: calculatedTotals.isInter,
        total: calculatedTotals.roundedTotal,
        taxableAmount: calculatedTotals.taxableAmount,
        totalTax: calculatedTotals.totalTax
      },
      bankDetails: {
        name: bankDetails.name,
        accountNo: bankDetails.accountNo,
        ifscCode: bankDetails.ifscCode,
        branch: bankDetails.branch,
        holderName: bankDetails.holderName
      }
    };
    setPreviewInvoiceData(previewData);
    setShowInvoicePreview(true);
  };

  const handleSubmit = async (event, chosenWatermark = 'none') => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }

    // First, validate stock availability for inventory items
    const stockValidation = await validateStockAvailability(items);

    if (!stockValidation.isValid) {
      alert('Stock Validation Error:\n\n' + stockValidation.errors.join('\n') + '\n\nPlease adjust quantities or check inventory levels.');
      return;
    }

    // Show warning if stock validation had issues but still allowed proceeding
    if (stockValidation.errors.length > 0) {
      const proceed = window.confirm('Warning:\n\n' + stockValidation.errors.join('\n') + '\n\nDo you want to proceed anyway?');
      if (!proceed) return;
    }

    const subtotal = calculatedTotals.taxableAmount;
    const totalDiscount = calculatedTotals.totalDiscount;
    const totalTax = calculatedTotals.totalTax;
    const isInter = calculatedTotals.isInter;
    const cgst = calculatedTotals.cgst;
    const sgst = calculatedTotals.sgst;
    const igst = calculatedTotals.igst;

    const taxDetails = {
      cgst,
      sgst,
      igst,
      isInterstate: isInter,
      total: calculatedTotals.roundedTotal,
      taxableAmount: calculatedTotals.taxableAmount,
      totalTax: calculatedTotals.totalTax
    };



    // Create the invoice object that matches your API structure
    if (!signature) {
      alert('Please provide a signature (either draw or upload) before saving.');
      return;
    }

    const invoice = {
      id: crypto.randomUUID(),
      invoiceNumber: generateInvoiceNumber(),
      invoiceDate: invoiceDate, // controlled yyyy-MM-dd
      documentType,
      theme,
      watermark: chosenWatermark || previewInvoiceData?.watermark || 'none',
      originalFor,

      // Include all company details (now dynamic)
      company: {
        name: companyDetails.name || '',
        logo: companyDetails.logo || '',
        address: companyDetails.address || '',
        state: companyDetails.state || '',
        mobile: companyDetails.mobile || '',
        gstin: companyDetails.gstin || '',
        email: companyDetails.email || '',
      },
      company_state: companyDetails.state || '',

      billTo: {
        name: billTo.name || '',
        address: billTo.address || '',
        state: billTo.state || '',
        mobile: billTo.mobile || '',
        gstin: billTo.gstin || '',
        customer_id: billTo.customer_id && !isNaN(billTo.customer_id) ? billTo.customer_id : null,
      },
      shipTo: shipTo.name || shipTo.address ? {
        name: shipTo.name || '',
        address: shipTo.address || '',
        mobile: shipTo.mobile || '',
        state: shipTo.state || '',
        gstin: shipTo.gstin || ''
      } : billTo, // Use shipTo if provided, otherwise use billTo
      items: items.map(item => ({
        ...item,
        qty: item.quantity || 0, // Map quantity to qty for backend
        discount: item.discount || 0, // Ensure discount is included
        tax: item.tax || 0, // Ensure tax is included
        rate: item.rate || 0,
        amount: item.amount || 0,
        inventoryId: item.inventoryId || null, // CRITICAL: Pass inventoryId for stock deduction
        hsn: item.hsn || '', // HSN code for GST compliance
      })),
      transportDetails, // Include transport details
      bankDetails,
      paymentDetails: {
        upiId: '', // To be filled by the business
        qrCode: '', // To be generated
        receivedAmount: 0,
        balance: taxDetails.total || 0,
      },
      taxDetails: {
        cgst: taxDetails.cgst || 0,
        sgst: taxDetails.sgst || 0,
        igst: taxDetails.igst || 0,
        isInterstate: taxDetails.isInterstate || false,
        taxableAmount: taxDetails.taxableAmount || 0,
        totalTax: (taxDetails.cgst || 0) + (taxDetails.sgst || 0) + (taxDetails.igst || 0),
      },
      subtotal: subtotal || 0,
      totalDiscount: totalDiscount || 0,
      totalAmount: taxDetails.total || 0,
      amountInWords: convertAmountToWords(taxDetails.total || 0),
      termsAndConditions: termsAndConditions || '',
      signature: signature || '', // dataURL; will be uploaded by parent before saving
      status: 'finalized', // Changed from 'draft' to 'finalized' to enable inventory deduction
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    console.log('Final invoice object:', invoice);
    console.log('Invoice items with inventory details:', items.map(item => ({
      name: item.name,
      quantity: item.quantity,
      inventoryId: item.inventoryId,
      hasInventoryId: !!item.inventoryId
    })));

    // Inventory deduction will happen automatically since status is 'finalized'

    handleSubmitInvoice(invoice);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSend = () => {
    alert('Send functionality coming soon!');
  };

  return (
    <Box sx={{
      maxWidth: '1600px',
      margin: '0 auto',
      padding: { xs: 2, sm: 3, md: 4 },
      width: '100%'
    }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => navigate('/invoices')}
          >
            Cancel
          </Button>
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={isLoading}
              form="invoice-form"
            >
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
            <Button variant="outlined" color="secondary" startIcon={<Print />} onClick={handlePrint}>Print</Button>
            <Button variant="outlined" color="success" startIcon={<Send />} onClick={handleSend}>Send</Button>
          </Box>
        </Box>

        <form id="invoice-form" onSubmit={handleSubmit}>
          <Typography variant="h6" gutterBottom>
            {initialData ? 'Edit Invoice' : 'Create New Invoice'}
          </Typography>

          {/* Company Details (Now Editable) */}
          <Box mb={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1" gutterBottom style={{ margin: 0 }}>
                Company Details
              </Typography>
              <Button 
                variant="outlined" 
                size="small" 
                startIcon={<PersonIcon />} 
                onClick={() => { fetchCompanyDetails(); fetchBankDetails(); }}
                sx={{ borderRadius: '20px', textTransform: 'none' }}
              >
                Fetch Account Details
              </Button>
            </Box>
            <Grid container spacing={3}>
              <Grid component="div" item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={companyDetails.name}
                  onChange={handleCompanyDetailsChange('name')}
                  variant="outlined"
                  required
                />
              </Grid>
              <Grid component="div" item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Company GSTIN"
                  value={companyDetails.gstin}
                  onChange={handleCompanyDetailsChange('gstin')}
                  placeholder="e.g., 08ABCDE1234F1Z5"
                  variant="outlined"
                />
              </Grid>
              <Grid component="div" item xs={12} sm={6} md={4}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="company-state-select-label">Company State (Origin)</InputLabel>
                  <Select
                    labelId="company-state-select-label"
                    value={companyDetails.state || ''}
                    label="Company State (Origin)"
                    onChange={handleCompanyDetailsChange('state')}
                  >
                    <MenuItem value="">
                      <em>-- Select Company State --</em>
                    </MenuItem>
                    {INDIAN_STATES.map((s) => (
                      <MenuItem key={s.code} value={s.name}>
                        {s.code} - {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid component="div" item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={companyDetails.address}
                  onChange={handleCompanyDetailsChange('address')}
                  multiline
                  rows={2}
                  variant="outlined"
                  required
                />
              </Grid>
              <Grid component="div" item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Mobile"
                  value={companyDetails.mobile}
                  onChange={handleCompanyDetailsChange('mobile')}
                  variant="outlined"
                />
              </Grid>
              <Grid component="div" item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={companyDetails.email}
                  onChange={handleCompanyDetailsChange('email')}
                  variant="outlined"
                  required
                />
              </Grid>
            </Grid>
          </Box>

          {/* Document Type & Details */}
          <Box mb={3}>
            <Typography variant="subtitle1" gutterBottom>
              Document Details
            </Typography>
            <Grid container spacing={3}>
              <Grid component="div" item xs={12} sm={3}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Document Type</InputLabel>
                  <Select
                    value={documentType}
                    onChange={handleDocumentTypeChange}
                    label="Document Type"
                  >
                    <MenuItem value="invoice">Invoice</MenuItem>
                    <MenuItem value="quotation">Quotation / Estimate</MenuItem>
                    <MenuItem value="debit_note">Debit Note</MenuItem>
                    <MenuItem value="credit_note">Credit Note</MenuItem>
                    <MenuItem value="delivery_challan">Delivery Challan</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid component="div" item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label={`${documentType === 'invoice' ? 'Invoice' : documentType === 'debit_note' ? 'Debit Note' : documentType === 'credit_note' ? 'Credit Note' : 'Challan'} Date`}
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  variant="outlined"
                />
              </Grid>
              <Grid component="div" item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Original For"
                  value={originalFor}
                  onChange={(e) => setOriginalFor(e.target.value)}
                  placeholder="e.g., Original, Duplicate"
                  variant="outlined"
                />
              </Grid>
              <Grid component="div" item xs={12} sm={3}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>PDF Layout Theme</InputLabel>
                  <Select
                    value={theme}
                    onChange={handleThemeChange}
                    label="PDF Layout Theme"
                  >
                    <MenuItem value="classic">Classic (Default)</MenuItem>
                    <MenuItem value="modern">Modern (Colorful)</MenuItem>
                    <MenuItem value="minimalist">Minimalist (Clean)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {/* Bill To Section */}
          <Box mb={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1">
                Bill To Details
              </Typography>
              <Button
                variant="outlined"
                startIcon={<PersonIcon />}
                onClick={() => setCustomerSelectorOpen(true)}
                size="small"
              >
                Select Customer
              </Button>
            </Box>
            <Grid container spacing={3}>
              <Grid component="div" item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Name"
                  value={billTo.name}
                  onChange={handleBillToChange('name')}
                  required
                />
              </Grid>
              <Grid component="div" item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Mobile"
                  value={billTo.mobile}
                  onChange={handleBillToChange('mobile')}
                />
              </Grid>
              <Grid component="div" item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="billto-state-select-label">State (Place of Supply)</InputLabel>
                  <Select
                    labelId="billto-state-select-label"
                    value={billTo.state || ''}
                    label="State (Place of Supply)"
                    onChange={handleBillToChange('state')}
                  >
                    <MenuItem value="">
                      <em>-- Select State / UT --</em>
                    </MenuItem>
                    {INDIAN_STATES.map((s) => (
                      <MenuItem key={s.code} value={s.name}>
                        {s.code} - {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid component="div" item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="GSTIN"
                  value={billTo.gstin}
                  onChange={handleBillToChange('gstin')}
                  placeholder="e.g., 27ABCDE1234F1Z5"
                />
              </Grid>
              <Grid component="div" item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={billTo.address}
                  onChange={handleBillToChange('address')}
                  multiline
                  rows={2}
                  required
                />
              </Grid>
              <Grid component="div" item xs={12}>
                {(() => {
                  const compState = companyDetails.state || (companyDetails.gstin ? getStateFromGSTIN(companyDetails.gstin)?.name : '');
                  const custState = billTo.state || (billTo.gstin ? getStateFromGSTIN(billTo.gstin)?.name : '') || shipTo.state || (shipTo.gstin ? getStateFromGSTIN(shipTo.gstin)?.name : '');
                  const isInter = isInterstateTransaction(
                    companyDetails.state || companyDetails.gstin,
                    billTo.state || billTo.gstin || shipTo.state || shipTo.gstin
                  );

                  return (
                    <Box sx={{
                      p: 1.5,
                      borderRadius: '8px',
                      backgroundColor: isInter ? '#e3f2fd' : '#e8f5e9',
                      border: `1px solid ${isInter ? '#cbd5e1' : '#e2e8f0'}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 1
                    }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: isInter ? '#1e3a8a' : '#14532d', fontSize: '0.88rem' }}>
                          {isInter 
                            ? 'Inter-State Transaction (IGST Applicable)' 
                            : 'Intra-State Transaction (CGST + SGST Applicable)'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#475569', display: 'block', mt: 0.2 }}>
                          Business Origin: <strong>{compState || 'Not Configured'}</strong> | Place of Supply: <strong>{custState || 'Same State'}</strong>
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ fontWeight: 700, px: 1.5, py: 0.5, bgcolor: '#ffffff', borderRadius: '4px', border: '1px solid #cbd5e1', color: isInter ? '#1e3a8a' : '#14532d', letterSpacing: '0.3px' }}>
                        {isInter ? 'IGST (100% Statutory Tax)' : 'CGST (50%) + SGST (50%)'}
                      </Typography>
                    </Box>
                  );
                })()}
              </Grid>
            </Grid>
          </Box>

          {/* Ship To Section */}
          <Box mb={3}>
            <Typography variant="subtitle1" gutterBottom>
              Ship To Details (Optional - if different from Bill To)
            </Typography>
            <Grid container spacing={3}>
              <Grid component="div" item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={shipTo.name}
                  onChange={handleShipToChange('name')}
                  placeholder="Leave empty to use Bill To details"
                />
              </Grid>
              <Grid component="div" item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Mobile Number"
                  value={shipTo.mobile}
                  onChange={handleShipToChange('mobile')}
                  placeholder="Leave empty to use Bill To mobile"
                />
              </Grid>
              <Grid component="div" item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={shipTo.address}
                  onChange={handleShipToChange('address')}
                  multiline
                  rows={2}
                  placeholder="Leave empty to use Bill To address"
                />
              </Grid>
              <Grid component="div" item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="shipto-state-select-label">Shipping State (Destination)</InputLabel>
                  <Select
                    labelId="shipto-state-select-label"
                    value={shipTo.state || ''}
                    label="Shipping State (Destination)"
                    onChange={handleShipToChange('state')}
                  >
                    <MenuItem value="">
                      <em>-- Leave empty to use Bill To state --</em>
                    </MenuItem>
                    {INDIAN_STATES.map((s) => (
                      <MenuItem key={s.code} value={s.name}>
                        {s.code} - {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid component="div" item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="GSTIN"
                  value={shipTo.gstin}
                  onChange={handleShipToChange('gstin')}
                  placeholder="Leave empty to use Bill To GSTIN"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Transport and Delivery Details for E-way Bill */}
          <Box mb={3}>
            <Typography variant="subtitle1" gutterBottom>
              Transport & Delivery Details (For E-way Bill - transactions over ₹50,000)
            </Typography>
            <Grid container spacing={3}>
              <Grid component="div" item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Vehicle Number"
                  value={transportDetails.vehicleNumber}
                  onChange={handleTransportDetailsChange('vehicleNumber')}
                  placeholder="e.g., RJ14GA1234"
                />
              </Grid>
              <Grid component="div" item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Vehicle Type"
                  value={transportDetails.vehicleType}
                  onChange={handleTransportDetailsChange('vehicleType')}
                  placeholder="e.g., Truck, Car, Bus"
                />
              </Grid>
              <Grid component="div" item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Driver Name"
                  value={transportDetails.driverName}
                  onChange={handleTransportDetailsChange('driverName')}
                  placeholder="Driver's full name"
                />
              </Grid>
              <Grid component="div" item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Driver Contact Number"
                  value={transportDetails.driverContact}
                  onChange={handleTransportDetailsChange('driverContact')}
                  placeholder="Driver's phone number"
                />
              </Grid>
              <Grid component="div" item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Eway Bill Number"
                  value={transportDetails.ewayBillNumber || ''}
                  onChange={handleTransportDetailsChange('ewayBillNumber')}
                  placeholder="Eway bill number (optional)"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Items Section */}
          <Box mb={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1">
                Items
              </Typography>
            </Box>
            <Box sx={{
              width: '100%',
              overflowX: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              backgroundColor: '#fafafa'
            }}>
              <Table sx={{ minWidth: '100%' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: '150px', width: '18%' }}>Item Name</TableCell>
                    <TableCell sx={{ minWidth: '160px', width: '15%' }}>Description</TableCell>
                    <TableCell sx={{ minWidth: '120px', width: '11%', fontSize: '0.95rem' }}>HSN Code</TableCell>
                    <TableCell sx={{ minWidth: '120px', width: '15%' }}>Quantity</TableCell>
                    <TableCell sx={{ minWidth: '60px', width: '6%' }}>Stock</TableCell>
                    <TableCell sx={{ minWidth: '70px', width: '8%' }}>Unit</TableCell>
                    <TableCell sx={{ minWidth: '140px', width: '15%' }}>Rate</TableCell>
                    <TableCell sx={{ minWidth: '100px', width: '10%', fontSize: '0.95rem' }}>GST %</TableCell>
                    <TableCell sx={{ minWidth: '100px', width: '10%', fontSize: '0.95rem' }}>Discount</TableCell>
                    <TableCell sx={{ minWidth: '130px', width: '14%', fontSize: '0.95rem' }}>Amount</TableCell>
                    <TableCell sx={{ minWidth: '50px', width: '4%' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ width: '18%' }}>
                        <TextField
                          fullWidth
                          value={item.name}
                          onChange={handleItemChange(index, 'name')}
                          required
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ width: '15%' }}>
                        <TextField
                          fullWidth
                          value={item.description}
                          onChange={handleItemChange(index, 'description')}
                          required
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ width: '11%' }}>
                        <TextField
                          fullWidth
                          value={item.hsn}
                          onChange={handleItemChange(index, 'hsn')}
                          placeholder="HSN"
                          size="medium"
                          inputProps={{ style: { fontSize: '0.95rem', padding: '10px 12px' } }}
                        />
                      </TableCell>
                      <TableCell sx={{ width: '15%' }}>
                        <TextField
                          type="number"
                          value={item.quantity ? item.quantity.toString() : ''}
                          onChange={(e) => handleSanitizedNumberChange(index, 'quantity', e.target.value)}
                          inputProps={{ min: 1, step: 1 }}
                          required
                          size="small"
                          placeholder="0"
                          sx={{
                            // Restoring default spinner/counter behavior
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ width: '6%' }}>
                        {item.inventoryId ? (
                          <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            <Typography
                              sx={{
                                fontSize: '0.9rem',
                                fontWeight: 'bold',
                                color: item.stockStatus === 'out_of_stock' ? 'error.main' :
                                  item.stockStatus === 'low_stock' ? 'warning.main' : 'success.main'
                              }}
                            >
                              {item.currentStock || 0}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ width: '8%' }}>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={normalizeUnit(item.unit || 'PCS')}
                            onChange={(e) => handleItemChange(index, 'unit')(e)}
                            displayEmpty
                          >
                            {COMMON_UNITS.map((unit) => (
                              <MenuItem key={unit} value={unit}>
                                {unit}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell sx={{ width: '15%', minWidth: '140px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {!item.reverseCalc ? (
                            <TextField
                              type="number"
                              value={item.rate ? item.rate.toString() : ''}
                              onChange={(e) => handleSanitizedNumberChange(index, 'rate', e.target.value)}
                              inputProps={{ min: 0, step: 1 }}
                              required
                              size="small"
                              placeholder="Base Rate"
                              sx={{
                                flex: 1,
                                '& .MuiInputBase-input': {
                                  fontSize: '0.95rem',
                                  padding: '10px 12px',
                                  fontWeight: '500'
                                },
                                '& input[type=number]': {
                                  '-moz-appearance': 'textfield',
                                },
                                '& input[type=number]::-webkit-outer-spin-button': {
                                  '-webkit-appearance': 'none',
                                  margin: 0,
                                },
                                '& input[type=number]::-webkit-inner-spin-button': {
                                  '-webkit-appearance': 'none',
                                  margin: 0,
                                },
                              }}
                            />
                          ) : (
                            <TextField
                              type="number"
                              placeholder="GST Incl. Rate"
                              value={item.reverseCalcValue || ''}
                              onChange={(e) => {
                                const value = parseFloat(e.target.value) || 0;
                                const newItems = [...items];
                                newItems[index].reverseCalcValue = e.target.value;
                                setItems(newItems);
                                handleReverseCalc(index, value);
                              }}
                              inputProps={{ min: 0.01, step: 0.01 }}
                              size="small"
                              sx={{
                                flex: 1,
                                '& .MuiInputBase-input': {
                                  fontSize: '0.95rem',
                                  padding: '10px 12px',
                                  fontWeight: '500',
                                  backgroundColor: '#f0f8ff'
                                },
                                '& input[type=number]': {
                                  '-moz-appearance': 'textfield',
                                },
                                '& input[type=number]::-webkit-outer-spin-button': {
                                  '-webkit-appearance': 'none',
                                  margin: 0,
                                },
                                '& input[type=number]::-webkit-inner-spin-button': {
                                  '-webkit-appearance': 'none',
                                  margin: 0,
                                },
                              }}
                            />
                          )}
                          <IconButton
                            size="small"
                            onClick={() => toggleReverseCalc(index)}
                            title={item.reverseCalc ? "Switch to Base Rate" : "Reverse Calculator (GST Inclusive)"}
                            sx={{
                              p: 0.5,
                              bgcolor: item.reverseCalc ? '#4caf50' : '#e0e0e0',
                              color: item.reverseCalc ? 'white' : 'inherit',
                              '&:hover': {
                                bgcolor: item.reverseCalc ? '#45a049' : '#d0d0d0',
                              },
                              border: item.reverseCalc ? '2px solid #2e7d32' : '1px solid #ccc',
                              fontSize: '14px',
                              fontWeight: 'bold'
                            }}
                          >
                            {item.reverseCalc ? '⇄' : '⇄'}
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ width: '10%' }}>
                        <TextField
                          type="number"
                          value={item.tax ? item.tax.toString() : ''}
                          onChange={(e) => handleSanitizedNumberChange(index, 'tax', e.target.value)}
                          placeholder="GST %"
                          inputProps={{ min: 0, max: 100, step: 0.01 }}
                          size="medium"
                          sx={{
                            '& .MuiInputBase-input': { fontSize: '0.95rem', padding: '10px 12px' },
                            '& input[type=number]': {
                              '-moz-appearance': 'textfield',
                            },
                            '& input[type=number]::-webkit-outer-spin-button': {
                              '-webkit-appearance': 'none',
                              margin: 0,
                            },
                            '& input[type=number]::-webkit-inner-spin-button': {
                              '-webkit-appearance': 'none',
                              margin: 0,
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ width: '10%' }}>
                        <TextField
                          type="number"
                          value={item.discount ? item.discount.toString() : ''}
                          onChange={(e) => handleSanitizedNumberChange(index, 'discount', e.target.value)}
                          placeholder="Disc %"
                          inputProps={{ min: 0, max: 100, step: 0.01 }}
                          size="medium"
                          sx={{
                            '& .MuiInputBase-input': { fontSize: '0.95rem', padding: '10px 12px' },
                            '& input[type=number]': {
                              '-moz-appearance': 'textfield',
                            },
                            '& input[type=number]::-webkit-outer-spin-button': {
                              '-webkit-appearance': 'none',
                              margin: 0,
                            },
                            '& input[type=number]::-webkit-inner-spin-button': {
                              '-webkit-appearance': 'none',
                              margin: 0,
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ width: '14%' }}>
                        <TextField
                          type="number"
                          value={item.amount ? item.amount.toString() : ''}
                          onChange={(e) => handleItemAmountChange(index, e.target.value)}
                          placeholder="Amount"
                          inputProps={{ min: 0, step: 1 }}
                          size="medium"
                          sx={{
                            '& .MuiInputBase-input': {
                              fontSize: '1rem',
                              padding: '10px 12px',
                              textAlign: 'right'
                            },
                            '& input[type=number]': {
                              '-moz-appearance': 'textfield',
                            },
                            '& input[type=number]::-webkit-outer-spin-button': {
                              '-webkit-appearance': 'none',
                              margin: 0,
                            },
                            '& input[type=number]::-webkit-inner-spin-button': {
                              '-webkit-appearance': 'none',
                              margin: 0,
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ width: '4%' }}>
                        <IconButton
                          color="error"
                          onClick={() => removeItem(index)}
                          disabled={items.length === 1}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Button
                startIcon={<AddIcon />}
                onClick={addItem}
              >
                Add Item
              </Button>
              <Button
                startIcon={<InventoryIcon />}
                onClick={() => setProductSelectorOpen(true)}
                variant="outlined"
                color="primary"
              >
                Select from Inventory
              </Button>
            </Box>
          </Box>

          {/* Bank Details Section (Now Editable) */}
          <Box mb={3}>
            <Typography variant="subtitle1" gutterBottom>
              Bank Details
            </Typography>
            <Grid container spacing={3}>
              <Grid component="div" item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Account Holder Name"
                  value={bankDetails.holderName}
                  onChange={handleBankDetailsChange('holderName')}
                  required
                />
              </Grid>
              <Grid component="div" item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Bank Name"
                  value={bankDetails.name}
                  onChange={handleBankDetailsChange('name')}
                  required
                />
              </Grid>
              <Grid component="div" item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Account Number"
                  value={bankDetails.accountNo}
                  onChange={handleBankDetailsChange('accountNo')}
                  required
                />
              </Grid>
              <Grid component="div" item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="IFSC Code"
                  value={bankDetails.ifscCode}
                  onChange={handleBankDetailsChange('ifscCode')}
                  required
                />
              </Grid>
              <Grid component="div" item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Branch"
                  value={bankDetails.branch}
                  onChange={handleBankDetailsChange('branch')}
                  required
                />
              </Grid>
            </Grid>
          </Box>

          {/* Terms and Conditions */}
          <Box mb={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
              <Typography variant="subtitle1" sx={{ mb: 0 }}>
                Terms and Conditions
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Box display="flex" alignItems="center" sx={{ border: '1px solid #e0e0e0', borderRadius: '16px', overflow: 'hidden' }}>
                  <Button 
                    size="small" 
                    variant="text" 
                    onClick={() => loadDefaultTerms('invoice')}
                    sx={{ textTransform: 'none', px: 2 }}
                  >
                    Auto-fill Invoice Terms
                  </Button>
                  <Tooltip title="Save current text as your permanent Invoice default">
                    <IconButton size="small" onClick={() => handleSaveDefaultTerms('invoice')} color="primary" sx={{ borderLeft: '1px solid #e0e0e0', borderRadius: 0 }}>
                      <SaveIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Box display="flex" alignItems="center" sx={{ border: '1px solid #e0e0e0', borderRadius: '16px', overflow: 'hidden' }}>
                  <Button 
                    size="small" 
                    variant="text" 
                    onClick={() => loadDefaultTerms('quotation')}
                    sx={{ textTransform: 'none', px: 2 }}
                  >
                    Auto-fill Quotation Terms
                  </Button>
                  <Tooltip title="Save current text as your permanent Quotation default">
                    <IconButton size="small" onClick={() => handleSaveDefaultTerms('quotation')} color="primary" sx={{ borderLeft: '1px solid #e0e0e0', borderRadius: 0 }}>
                      <SaveIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Button 
                  size="small" 
                  variant="outlined" 
                  color="error"
                  onClick={() => setTermsAndConditions('')}
                  startIcon={<DeleteIcon fontSize="small" />}
                  sx={{ textTransform: 'none', borderRadius: '16px', px: 2 }}
                  title="Clear Terms"
                >
                  Clear
                </Button>
              </Box>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={termsAndConditions}
              onChange={(e) => setTermsAndConditions(e.target.value)}
            />
          </Box>

          {/* Signature Section - Required */}
          <Box mb={3}>
            <Typography variant="subtitle1" gutterBottom>
              Signature - Sign below or upload an image
            </Typography>
            <Box sx={{ border: '1px solid #ccc', borderRadius: '6px', p: 1, position: 'relative' }}>
              {showSignatureImage && signature && signature.length > 0 && (
                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fff', zIndex: 1, borderRadius: '5px' }}>
                  <img src={signature.startsWith('/') ? `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${signature}` : signature} alt="Saved Signature" style={{ maxHeight: '160px', maxWidth: '100%' }} />
                </div>
              )}
              <canvas ref={canvasRef} style={{ width: '100%', height: '180px', display: 'block', position: 'relative', zIndex: 2 }} />
            </Box>
            <Box mt={1} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button variant="outlined" size="small" onClick={clearSignature}>Clear</Button>
              <Button variant="outlined" size="small" component="label">
                Upload Signature
                <input type="file" hidden accept="image/jpeg, image/jpg" onChange={handleSignatureUpload} />
              </Button>
            </Box>
            {!signature && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                * A signature (drawn or uploaded) is required to save.
              </Typography>
            )}
          </Box>

          {/* Invoice Summary, Total Price & GST Breakdown Preview Section (After Signature) */}
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              mb: 4,
              p: { xs: 2.5, sm: 3.5 },
              bgcolor: '#ffffff',
              border: '1px solid #cbd5e1',
              borderRadius: '10px'
            }}
          >
            {/* Header: Executive Accounting Summary */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexWrap: 'wrap',
              gap: 2,
              pb: 2.5,
              borderBottom: '1px solid #e2e8f0'
            }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.15rem', color: '#0f172a', letterSpacing: '-0.2px' }}>
                  Invoice Summary & Tax Ledger
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.85rem', mt: 0.3 }}>
                  Pre-creation statutory tax breakdown and net payable computation
                </Typography>
              </Box>

              <Button
                variant="outlined"
                color="primary"
                startIcon={<VisibilityIcon />}
                onClick={handleOpenPreview}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 2.5,
                  py: 0.9,
                  fontSize: '0.9rem',
                  borderColor: '#0f172a',
                  color: '#0f172a',
                  '&:hover': {
                    bgcolor: '#f1f5f9',
                    borderColor: '#0f172a'
                  }
                }}
              >
                Preview Invoice
              </Button>
            </Box>

            {/* GST Supply Ledger Strip */}
            <Box sx={{
              my: 2.5,
              p: 1.8,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1.5,
              bgcolor: '#f8fafc',
              border: '1px solid #e2e8f0'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{
                  px: 1.2,
                  py: 0.4,
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  bgcolor: calculatedTotals.isInter ? '#1e3a8a' : '#14532d',
                  color: '#ffffff',
                  letterSpacing: '0.5px'
                }}>
                  {calculatedTotals.isInter ? "INTER-STATE SUPPLY" : "INTRA-STATE SUPPLY"}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.88rem' }}>
                  {calculatedTotals.isInter
                    ? `Place of Supply: ${calculatedTotals.destState || 'Customer State'} (Origin: ${calculatedTotals.originState || 'Company State'})`
                    : `Place of Supply: ${calculatedTotals.originState || calculatedTotals.destState || 'Same State'}`
                  }
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: '#475569', fontWeight: 600, fontSize: '0.82rem' }}>
                {calculatedTotals.isInter ? 'Statutory Rule: Integrated Tax (IGST)' : 'Statutory Rule: Central Tax (CGST) + State Tax (SGST)'}
              </Typography>
            </Box>

            {/* Two-Column Grid: Left Stats & Slabs | Right Calculation */}
            <Grid container spacing={3}>
              {/* Left Column: Slabs & Metadata */}
              <Grid item xs={12} md={6}>
                <Box sx={{
                  p: 2.5,
                  bgcolor: '#ffffff',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mb: 2, textTransform: 'uppercase', letterSpacing: '0.6px', fontSize: '0.75rem' }}>
                      Item Count & Tax Distribution
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.2, py: 0.6, borderBottom: '1px solid #f1f5f9' }}>
                      <Typography variant="body2" sx={{ color: '#64748b' }}>Distinct Line Items</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                        {calculatedTotals.itemCount} {calculatedTotals.itemCount === 1 ? 'Item' : 'Items'}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, py: 0.6, borderBottom: '1px solid #f1f5f9' }}>
                      <Typography variant="body2" sx={{ color: '#64748b' }}>Total Quantity / Units</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                        {calculatedTotals.totalQty} Units
                      </Typography>
                    </Box>

                    {/* Tax Rate Slabs Breakdown */}
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#334155', display: 'block', mb: 1.2, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      GST Rate Slabs
                    </Typography>

                    {Object.keys(calculatedTotals.taxBreakdown).length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {Object.entries(calculatedTotals.taxBreakdown).map(([rate, info]) => (
                          <Box
                            key={rate}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              p: 1.2,
                              borderRadius: '6px',
                              bgcolor: '#f8fafc',
                              border: '1px solid #e2e8f0'
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.85rem' }}>
                                {rate}% GST
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                Taxable: ₹{info.taxable.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                              ₹{info.tax.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          No GST applied or 0% tax selected
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Verification Status */}
                  <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>Document:</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#0f172a', bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: '4px' }}>
                        {(documentType || 'invoice').toUpperCase()}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>Signature:</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: signature ? '#15803d' : '#b45309', bgcolor: signature ? '#f0fdf4' : '#fffbeb', border: `1px solid ${signature ? '#bbf7d0' : '#fde68a'}`, px: 1, py: 0.3, borderRadius: '4px' }}>
                        {signature ? 'Attached' : 'Pending Signature'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* Right Column: Financial Calculation */}
              <Grid item xs={12} md={6}>
                <Box sx={{
                  p: 2.8,
                  bgcolor: '#ffffff',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.2
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.6px', fontSize: '0.75rem' }}>
                    Financial Summary
                  </Typography>

                  {/* Subtotal (Taxable Amount) */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.3 }}>
                    <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.9rem' }}>
                      Taxable Amount (Subtotal)
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.95rem' }}>
                      ₹ {calculatedTotals.taxableAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>

                  {/* Item Discounts (if any) */}
                  {calculatedTotals.totalDiscount > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.3 }}>
                      <Typography variant="body2" sx={{ color: '#15803d', fontSize: '0.9rem' }}>
                        Total Discount
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#15803d', fontSize: '0.95rem' }}>
                        - ₹ {calculatedTotals.totalDiscount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 0.5, borderColor: '#f1f5f9' }} />

                  {/* GST Tax Breakdown */}
                  {calculatedTotals.isInter ? (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.3 }}>
                      <Box>
                        <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600, fontSize: '0.9rem' }}>
                          Integrated GST (IGST)
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Inter-State Tax
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.95rem' }}>
                        ₹ {calculatedTotals.igst.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.2 }}>
                        <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.9rem' }}>
                          Central GST (CGST)
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.95rem' }}>
                          ₹ {calculatedTotals.cgst.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.2 }}>
                        <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.9rem' }}>
                          State GST (SGST)
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.95rem' }}>
                          ₹ {calculatedTotals.sgst.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                    </>
                  )}

                  {/* Total Tax Bar */}
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: '#f8fafc',
                    px: 1.5,
                    py: 1,
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                      Total Applied GST
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                      ₹ {calculatedTotals.totalTax.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>

                  {/* Round Off (if any) */}
                  {calculatedTotals.roundOff !== 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.2 }}>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        Round Off
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>
                        {calculatedTotals.roundOff > 0 ? `+ ₹ ${calculatedTotals.roundOff.toFixed(2)}` : `- ₹ ${Math.abs(calculatedTotals.roundOff).toFixed(2)}`}
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 0.6, borderColor: '#cbd5e1' }} />

                  {/* Grand Total Box */}
                  <Box sx={{
                    p: 2,
                    borderRadius: '8px',
                    bgcolor: '#0f172a',
                    color: '#ffffff',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.8rem', letterSpacing: '0.5px', color: '#94a3b8' }}>
                        Total Amount Payable
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.72rem' }}>
                        (Inclusive of All Statutory Taxes)
                      </Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#ffffff', letterSpacing: '-0.3px' }}>
                      ₹ {calculatedTotals.roundedTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>

                  {/* Amount In Words */}
                  {calculatedTotals.words && (
                    <Box sx={{ mt: 0.5, p: 1.2, bgcolor: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                      <Typography variant="caption" sx={{ color: '#475569', display: 'block', lineHeight: 1.4 }}>
                        <strong style={{ color: '#0f172a' }}>Amount in words:</strong> {calculatedTotals.words}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Action Buttons */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            mt: 4,
            pt: 3,
            borderTop: '1px solid #e2e8f0',
            flexWrap: 'wrap'
          }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSubmit}
              sx={{
                px: 4,
                py: 1.4,
                fontSize: '1rem',
                fontWeight: 700,
                minWidth: '200px',
                borderRadius: '8px',
                textTransform: 'none'
              }}
              disabled={!signature}
            >
              Save Invoice
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="large"
              startIcon={<VisibilityIcon />}
              onClick={handleOpenPreview}
              sx={{
                px: 3,
                py: 1.4,
                fontSize: '1rem',
                fontWeight: 600,
                minWidth: '180px',
                borderRadius: '8px',
                textTransform: 'none'
              }}
            >
              Preview Invoice
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              size="large"
              onClick={() => navigate('/invoices')}
              sx={{
                px: 3,
                py: 1.4,
                fontSize: '1rem',
                fontWeight: 600,
                minWidth: '130px',
                borderRadius: '8px',
                textTransform: 'none',
                borderColor: '#cbd5e1'
              }}
            >
              Cancel
            </Button>
          </Box>
        </form>

        {/* Product Selector Modal */}
        <ProductSelector
          open={productSelectorOpen}
          onClose={() => setProductSelectorOpen(false)}
          onSelectProduct={handleProductSelect}
        />

        {/* Customer Selector Modal */}
        <CustomerSelector
          open={customerSelectorOpen}
          onClose={() => setCustomerSelectorOpen(false)}
          onSelectCustomer={handleCustomerSelect}
        />

        {/* Pre-Creation Full Document Preview Modal */}
        {showInvoicePreview && previewInvoiceData && (
          <ViewInvoiceModal
            show={showInvoicePreview}
            onClose={() => setShowInvoicePreview(false)}
            invoice={previewInvoiceData}
            onConfirm={(selectedWatermark) => {
              setShowInvoicePreview(false);
              handleSubmit(null, selectedWatermark);
            }}
          />
        )}
      </Paper>
    </Box>
  );
};

export default CreateInvoiceForm;
